// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { ErrorInformationStorage } from "../../Commons/ErrorInformationStorage.ts";
import { Resources } from "../../Commons/properties/Resources.ts";
import { QuickTableColumnType, QuickTableEditingInfo } from "../elements/QuickTable/QuickTableMisc.js";
import { BaseItem } from "./BaseItem.js";
import { ColumnParams } from "./ColumnParams.ts";
import { OperationType } from "../../Utils/Trading/OperationType.ts";
import { OrderType } from "../../Utils/Trading/OrderType.ts";
import { ThemeManager } from "../misc/ThemeManager.js";
import { AccountType } from "../../Utils/Account/AccountType.ts";
import { DateTimeUtils } from "../../Utils/Time/DateTimeUtils.ts";
import { RulesSet } from "../../Utils/Rules/RulesSet.ts";
import { DynProperty } from "../../Commons/DynProperty.ts";
import { SLTPTrigger } from "../../Utils/SlTpTrigger.ts";
import { GeneralSettings } from "../../Utils/GeneralSettings/GeneralSettings.ts";
import { ProfitCalculator } from "../../Commons/cache/ProfitCalculator.ts";
import { RiskPlan } from "../../Commons/cache/RiskPlan.ts";
import { Instrument } from "../../Commons/cache/Instrument.ts";
import { InstrumentUtils } from "../../Utils/Instruments/InstrumentUtils.ts";
import { OffsetModeViewEnum } from "../../Utils/Trading/OffsetModeViewEnum.ts";
import { IsAllowed } from "../../Commons/IsAllowed.ts";
import { DataCache } from "../../Commons/DataCache.ts";
import { DateTimeConvertor } from "../../Utils/Time/DateTimeConvertor.ts";
import { PositionFormatter } from "../../Commons/cache/Formatters/PositionFormatter.ts";
import { OrderUtils } from "../../Utils/Trading/OrderUtils.ts";
import { Position } from "../../Commons/cache/Position.ts";

export let PositionItem = function (pos, sessionSettings, isNetQty)
{
    BaseItem.call(this, sessionSettings);

    // предлагаю небольшой рефакторинг в конструкторе с целью избавиться от 4х проверок if (pos)
    if (!pos) return

    this.FPosition = pos;

    this.NetQty = !!isNetQty;   // #92796 пункт 4 a. ii. доки: https://docs.google.com/document/d/1y4W7_QHbI4siuOD4OwrBucXWxQdbbqJoiw8bfEKvDhI/edit

    this.ItemId = PositionItem.GetItemId(pos);

    this.Disabled = pos.IsPendingExerciseOptionStatus()    // #93127

    this.useInstrumentCurrencyForExposition = false;
    this.userGroupName = null;

    if (pos.SuperPositionId != '-1')     // SuperPosition or its trade (FIFO) #80010
    {
        if (pos.SuperPositionId != pos.PositionId)
        {
            this.isSubItem = true

            let superPos = DataCache.getPositionById(pos.SuperPositionId)
            if (superPos)   // от этого сеттинга можно избавиться доставая значение ExerciseOptionStatus в Positions но мне это видится менее правильным
                this.Disabled = superPos.IsPendingExerciseOptionStatus()
        }
        this.superItemId = pos.SuperPositionId

        let controlType = pos.isSuperPosition ? DynProperty.EXPAND_SUPER_POSITION_BUTTON_AND_TEXT : DynProperty.COMPONENT_OF_SUPER_POSITION_TEXT
        this.QuickTableEditingInfoMap[0] = (function ()
        {
            return new QuickTableEditingInfo(controlType);
        })();
    }

    this.QuickTableEditingInfoMap[45] = (function ()
    {
        return new QuickTableEditingInfo(DynProperty.CLOSE_BUTTON);
    })();
    let tm = ThemeManager.CurrentTheme;
    this.CellForeColorMap[0] = tm.TableGoldColor;//"#EDC234";
    this.CellForeColorMap[2] = tm.TableGoldColor;//"#EDC234";
    this.CellForeColorMap[7] = pos.BuySell === OperationType.Buy ? tm.TableLongBuyColor : tm.TableShortSellColor;//"#EDC234";
}
PositionItem.prototype = Object.create(BaseItem.prototype);

PositionItem.NOT_AVAILABLE = "N/A";

PositionItem.prototype.ColumnCount = function ()
{
    return PositionItem.columnsParams.length
}

PositionItem.prototype.getColumnData = function (column)
{
    let o = this.getColumnValue(column)
    let FormattedValue = null

    let pos = this.FPosition
    let ins = pos.Instrument
    let acc = pos.Account
    let dc = pos.DataCache
    let sess = this.sessionSettings
    let ShowLots = GeneralSettings.View.displayAmountInLots()

    switch (column)
    {
        case 2:
            FormattedValue = dc.formatVolume(ins, Math.abs(o), ShowLots, pos.ProductType)
            break

        case 3:
            FormattedValue = PositionFormatter.PriceStr(pos);
            break

        case 4:
            if (o && o.getTime() > 0)
                FormattedValue = DateTimeUtils.FormatToDate(o)
            else
                FormattedValue = '';
            break

        case 5:
            FormattedValue =
                pos.StrikePrice > 0
                    ? ins.formatPrice(pos.StrikePrice)
                    : ''
            break

        case 7:
            FormattedValue = o.toString()
            break

        case 8:
            FormattedValue = PositionFormatter.DateTimeStr(pos);
            break

        case 9:
            FormattedValue = PositionFormatter.CurrentPriceStr(pos);
            break

        case 10:
            FormattedValue = PositionFormatter.StopLossPriceStr(pos);
            break

        case 11:
            FormattedValue = PositionFormatter.TakeProfitPriceStr(pos);
            break

        case 12:
            FormattedValue =
                pos.lastPriceUpdated
                    ? acc.formatPrice(pos.getSettlementProfit(true))
                    : PositionItem.NOT_AVAILABLE
            break

        case 13:
            FormattedValue = acc.formatPrice(pos.GetSwaps(true))
            break

        case 14:
            if (this.useInstrumentCurrencyForExposition)
            {
                let asset = dc.GetAssetByName(ins.Exp2)
                if (asset)
                    FormattedValue = asset.formatPrice(pos.getExpositionValue(true))
                else
                    FormattedValue = pos.getExpositionValue(true) + ' ' + ins.Exp2
            }
            else
            {
                FormattedValue = acc.formatPrice(pos.getExpositionValue(true))
            }
            break

        case 15:
            FormattedValue = PositionFormatter.CommissionStr(pos);
            break

        case 19:
            FormattedValue =
                pos.SLOrder &&
                    !isNaN(pos.SLOrder.Price) &&
                    pos.SLOrder.OrderType === OrderType.TrailingStop
                    ? ins.formatPrice(pos.SLOrder.TrStopOffset)
                    : ''
            break

        case 22:
            FormattedValue = acc.formatPrice(pos.HoldMargin)
            break

        case 23:
            if (!pos.lastPriceUpdated)
            {
                FormattedValue = PositionItem.NOT_AVAILABLE
            }
            else
            {
                if (typeof o === 'number')
                {
                    FormattedValue =
                        ins.formatOffset(o) +
                        ' ' +
                        Resources.getResource(
                            GeneralSettings.TradingDefaults.ShowOffsetIn === OffsetModeViewEnum.Points
                                ? 'panel.positions.PL_ticks.points'
                                : 'panel.positions.PL_ticks.ticks')
                }
                else
                    FormattedValue = ''
            }
            break

        case 24:
            FormattedValue = PositionFormatter.ProfitStr(pos);
            break

        case 27:
            FormattedValue = acc.formatPrice(pos.getInitMargin(true))
            break

        case 28:
            FormattedValue = acc.formatPrice(pos.getMaintMargin(true))
            break

        case 29:
            let netProfitInAccCurr = pos.getNetProfit(true)
            FormattedValue =
                // !isNaN(netProfitInAccCurr)
                acc.formatPrice(pos.getInitMargin(true) - pos.getMaintMargin(true) + netProfitInAccCurr)
            // : Resources.getResource('general.Calculating')
            break

        case 30:
            let posValue = pos.getPositionValue(true)

            if (isNaN(posValue))
                FormattedValue = PositionItem.NOT_AVAILABLE
            else
                if (this.useInstrumentCurrencyForExposition)
                {
                    let asset = dc.GetAssetByName(pos.Exp2)
                    FormattedValue =
                        asset
                            ? asset.formatPrice(posValue)
                            : posValue + ' ' + pos.Exp2
                }
                else
                    FormattedValue = acc.formatPrice(posValue)
            break

        case 34:
            FormattedValue = acc.formatPrice(o)
            break

        case 48:
            if (o !== 0)
                FormattedValue = dc.formatVolume(ins, Math.abs(o), ShowLots, pos.ProductType)
            else
                FormattedValue = "";
            break

        case 50:
            FormattedValue = OrderUtils.GetFormattedLeverage(o, true) || Resources.getResource("general.N_A");
            break;
    }

    if (FormattedValue === null)
    {
        if (typeof o === 'number')
            FormattedValue = ins.formatPrice(o)
        else
            FormattedValue = o ? o.toString() : ''
    }

    return { Value: o, FormattedValue: FormattedValue }
}

PositionItem.prototype.getColumnValue = function (column)
{
    let customColumnValues = this.customColumnValues
    if (column >= PositionItem.columnsParams.length && customColumnValues.hasOwnProperty(column))
        return customColumnValues[column]

    let IV = 0;
    let delta = 1
    let gamma = 0
    let vega = 0
    let theta = 0
    let rho = 0

    let pos = this.FPosition
    let buy = pos.BuySell === OperationType.Buy
    let ins = pos.Instrument
    let acc = pos.Account
    let dc = pos.DataCache
    let sess = this.sessionSettings
    let showLots = GeneralSettings.View.displayAmountInLots()
    // TODO.
    let UseAccCurrency = PositionItem.UseAccCurrency(pos)

    switch (column)
    {
        case 0:
            return PositionFormatter.SymbolStr(pos);

        case 1:
            return PositionFormatter.AccountStr(pos);

        case 2:
            // this.NetQty  пункт 4 a. ii. доки: https://docs.google.com/document/d/1y4W7_QHbI4siuOD4OwrBucXWxQdbbqJoiw8bfEKvDhI/edit
            let sign = PositionItem.GetPositionSign(pos, this.NetQty);
            return PositionItem.GetPositionAmount(pos) * sign;

        case 3:
            return PositionFormatter.Price(pos);

        case 4:
            if (pos !== null && pos.Instrument !== null && pos.Instrument.ExpDateReal.getFullYear() > 2000)
                return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(pos.Instrument.ExpDateReal, pos.DataCache);
            else
                return DateTimeUtils._ZeroTime;

        case 5:
            return pos.StrikePrice > 0 ? pos.StrikePrice : 0

        case 6:
            return PositionFormatter.PositionId(pos);

        case 7:
            return PositionFormatter.OperationStr(pos);

        case 8:
            return PositionFormatter.DateTime(pos);

        case 9:
            return PositionFormatter.CurrentPrice(pos);

        case 10:
            return PositionFormatter.StopLossPrice(pos);

        case 11:
            return PositionFormatter.TakeProfitPrice(pos);

        case 12:
            return pos.lastPriceUpdated ? pos.getSettlementProfit(UseAccCurrency) : 0

        case 13:
            return pos.GetSwaps(UseAccCurrency)

        case 14:
            return pos.getExpositionValue(UseAccCurrency)

        case 15:
            return PositionFormatter.Commission(pos, UseAccCurrency);

        case 16:
            return pos.Comment

        case 17:
            return pos.Route

        case 18:
            return InstrumentUtils.getInstrumentTypeStringLocalized(ins.InstrType)

        case 19:
            return PositionFormatter.TrStopOffset(pos);

        case 20:
            return acc.userLogin

        case 21:
            return pos.GetInstrumentDescriptionValue()

        case 22:
            return UseAccCurrency
                ? pos.HoldMargin
                : pos.HoldMargin / acc.getCrossPrice()

        case 23:
            return pos.lastPriceUpdated
                ? Instrument.TickValue(ins, pos.CurPriceClose, pos.Price, buy ? 1 : -1)
                : 0

        case 24:
            return PositionFormatter.Profit(pos, UseAccCurrency);

        case 25:
            return pos.IsGroupPart ? pos.OrderGroupId.ToString() : ''

        case 26:
            return this.userGroupName || ''

        case 27:
            return pos.getInitMargin(UseAccCurrency)

        case 28:
            return pos.getMaintMargin(UseAccCurrency)

        case 29:
            return pos.getInitMargin(UseAccCurrency) - pos.getMaintMargin(UseAccCurrency) + pos.getNetProfit(UseAccCurrency)

        case 30:
            return pos.getPositionValue(UseAccCurrency)

        // TODO.
        case 31:
            return 0
        //return pos.getSettlementProfit(false) / dc.CrossRateCache.GetCrossPrice(pos.Exp2, CrossRateCalculationType.BID_ASK_2)

        case 32:
            return -pos.GetCommissionFromSettlement(false)

        // TODO.
        case 33:
            return 0
        /*
        return pos.lastPriceUpdated 
          ? FPosition.getSettlementProfit(false) / dc.CrossRateCache.GetCrossPrice(pos.Exp2) - pos.GetCommissionFromSettlement(false)
          : 0
        */

        case 34:
            return UseAccCurrency
                ? this.GetSLValue()
                : this.GetSLValue() / acc.getCrossPrice()

        // TODO.
        case 35:
        case 36:
            return pos.RealizedPL

        case 37:
            return delta

        case 38:
            return gamma

        case 39:
            return vega

        case 40:
            return theta

        case 41:
            return IV

        case 42:
            return rho

        case 43:
            return InstrumentUtils.GetLocalizedProductType(ins, pos.ProductType)

        case 44:
            return dc.formatVolume(
                ins,
                pos.AmountAvailableForSell * (showLots ? 1 : ins.getLotSize(/*TODO*/)),
                showLots,
                pos.ProductType)

        case 46:
            return ins.TradingExchange || "";

        case 47:
            let order = pos.SLOrder

            return order && order.StopLimit && order.OrderType !== OrderType.TrailingStop ? pos.SLOrder.Price : null;

        case 48:
            let startOfDaySign = PositionItem.GetPositionSign(pos, this.NetQty);
            return PositionItem.GetPositionStartOfDayAmount(pos) * startOfDaySign;

        case 49:
            return ins.AssetName ? ins.AssetName : "";

        case 50:
            return pos.Leverage ? pos.Leverage : "";
    }

    return ''
}

PositionItem.prototype.getFormatTotalValue = function (column, value, precisionMode = false, totalCurrencyAfterFiltration = null)
{
    let formattedValue = "";

    let pos = this.FPosition
    let dc = pos.DataCache
    let asset = dc.GetAssetByName(totalCurrencyAfterFiltration || dc.userBaseCurrency)

    switch (column)
    {
        case 2:
            if (precisionMode)
                return 2;
            formattedValue = value.toFixed(2)
            break

        case 12:
        case 13:
        case 14:
        case 15:
        case 24:
        case 30:
            if (precisionMode)
                return asset.Point;
            formattedValue = asset.formatPrice(value)
            break
    }

    return formattedValue;
}

// TODO. Ugly. Static modification in each item.
PositionItem.prototype.GetColumnParams = function (column)
{
    let result = PositionItem.columnsParams[column]

    let isHidden = Resources.isHidden(result.HeaderKey)
    // TODO. Refactor. Ugly.
    if (
        column === 16 ||
        column === 17 ||
        column === 19 ||
        column === 22 ||
        column === 25 ||
        column === 26 ||
        column === 27 ||
        column === 28 ||
        column === 29 ||
        (column >= 31 && column < 43))
        isHidden = true

    if (column == 34)
        isHidden = !DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SLTP);

    if (column === 43)
        isHidden = !RiskPlan.showProductType;

    if ((column === 10 || column === 11 || column === 47) && IsAllowed.IsProtectiveOrders())
        isHidden = true // #108200 doc 4.2

    result.Hidden = isHidden
    result.MenuIndex = this.GetMenuIndex(result.HeaderKey)

    return result
}

PositionItem.prototype.fireUpdate = function ()
{

}

PositionItem.prototype.GetSLValue = function ()
{
    var slRiskValue = 0;

    try
    {
        let pos = this.FPosition
        if (pos != null)
        {
            //#32401
            if (pos.SLOrder != null)
            {
                let ins = pos.Instrument;
                let acc = pos.Account;
                let Qty = pos.Amount;
                let isBuy = pos.BuySell === OperationType.Buy;
                let price = pos.Price;
                let slPrice = pos.SLOrder.Price
                const openCrossPrice = pos.OpenCrossPrice;

                slRiskValue = ProfitCalculator.CalculateSLTP(Qty, slPrice, price, ins, acc, isBuy, openCrossPrice, pos.ProductType)
            }
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
    }

    return slRiskValue;
}


PositionItem.prototype.GetCurrentAccount = function ()
{
    return this.FPosition.Account;
}
PositionItem.prototype.GetCurrentInstrument = function ()
{
    return this.FPosition.Instrument;
}

PositionItem.prototype.InstrumentName = function ()
{
    return this.FPosition.Instrument.GetInteriorID();
};

PositionItem.prototype.DisplayInstrumentName = function ()
{
    return this.FPosition.Instrument.DisplayName();
};

PositionItem.prototype.isModifyAllowed = function (columnIndex)
{
    let modifyAllow = IsAllowed.IsPositionModifyingAllowed(this.FPosition).Allowed

    if (!modifyAllow)
        return modifyAllow

    let acc = this.GetCurrentAccount(),
        ins = this.GetCurrentInstrument()

    if (columnIndex == PositionItem.SL_COL_INDEX)
        modifyAllow = IsAllowed.IsSLTPAllowed(ins, acc, true).Allowed && !this.FPosition.IsActivated
    else if (columnIndex == PositionItem.TP_COL_INDEX)
        modifyAllow = IsAllowed.IsSLTPAllowed(ins, acc, false).Allowed
    else if (columnIndex == PositionItem.PRODUCTTYPE_COL_INDEX)
        modifyAllow = IsAllowed.IsPositionProductTypeModifyingAllowed([this.FPosition]).Allowed

    return modifyAllow
}

PositionItem.columnsParams =
    [
  /*0*/   new ColumnParams("panel.positions.Symbol", 70, 0, false, true, true, false, /*beginGroupFlag:*/ true),
        new ColumnParams("panel.positions.Account", 70, 0, false, false, true, false),
        new ColumnParams("panel.positions.Qunatity", 70, QuickTableColumnType.COL_MORELESS_NUMERIC, true, true, false),
        new ColumnParams("panel.positions.Basis", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
  /*4*/   new ColumnParams("panel.positions.ExpDate", 70, 0, false, false, true),
        new ColumnParams("panel.positions.strike", 70, 0, false, false, true),
        new ColumnParams("panel.positions.pos_number", 70, 0, false, false, false, false, /*beginGroupFlag:*/ true),
        new ColumnParams("panel.positions.Operation", 70, 0, false, true, true),
        new ColumnParams("panel.positions.date_time", 70, QuickTableColumnType.COL_DATE_SORT, false, false, false),
        new ColumnParams("panel.positions.cur_price", 70, QuickTableColumnType.COL_UPDOWN_NUMERIC, false, true, false),
  /*10*/  new ColumnParams("panel.positions.SL", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false, null, null, null, true),
        new ColumnParams("panel.positions.TP", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false, null, null, null, true),
        new ColumnParams("panel.positions.profit_usd", 70, QuickTableColumnType.COL_MORELESS_NUMERIC, true, false, false),
        new ColumnParams("panel.positions.swaps", 70, QuickTableColumnType.COL_MORELESS_NUMERIC, true, false, false),
        new ColumnParams("panel.positions.exposition_usd", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, true, false, false),
  /*15*/  new ColumnParams("panel.positions.commissions", 70, QuickTableColumnType.COL_MORELESS_NUMERIC, true, true, false),
        new ColumnParams("panel.positions.comments", 70, 0, false, false, false),
        new ColumnParams("panel.positions.route", 70, 0, false, false, false),
        new ColumnParams("panel.positions.type", 70, 0, false, true, true),
        new ColumnParams("panel.positions.tr", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
  /*20*/  new ColumnParams("panel.positions.Login", 70, 0, false, false, true),
        new ColumnParams("panel.positions.SymbolDescription", 70, 0, false, false, true),
        new ColumnParams("panel.positions.UsedMargin", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false, false),
        new ColumnParams("panel.positions.PL_ticks", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.Net_PL", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, true, true, false),
  /*25*/  new ColumnParams("panel.positions.GroupId", 70, 0, false, false, true),
        new ColumnParams("panel.positions.UserGroup", 70, 0, false, false, true),
        new ColumnParams("panel.positions.InitReq", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.MaintReq", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.MarginSuplus", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
  /*30*/  new ColumnParams("panel.positions.PositionValue", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, true, false, false),
        // +++ new total gross/net/fee
        new ColumnParams("panel.positions.profit_usd.InstrumentCurrency", 70, QuickTableColumnType.COL_MORELESS_NUMERIC, false, true, false),
        new ColumnParams("panel.positions.commissions.InstrumentCurrency", 70, QuickTableColumnType.COL_MORELESS_NUMERIC, false, true, false, false, /*beginGroupFlag:*/ true),
        new ColumnParams("panel.positions.Net_PL.InstrumentCurrency", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
        new ColumnParams("panel.positions.SL_Value", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
  /*35*/  new ColumnParams("panel.positions.RealizedPnL", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.RealizedPnL.InstrumentCurrency", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.Delta", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
        new ColumnParams("panel.positions.Gamma", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
        new ColumnParams("panel.positions.Vega", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
  /*40*/  new ColumnParams("panel.positions.Theta", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
        new ColumnParams("panel.positions.IV", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.Rho", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
        new ColumnParams("panel.positions.ProductType", 70, QuickTableColumnType.COL_DEFAULT, false, false, false, null, null, null, true),
        new ColumnParams("panel.positions.AvailableForSell", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false),
   /*45*/ new ColumnParams("panel.positions.Close", 70, QuickTableColumnType.COL_DEFAULT, false, true, false),
        new ColumnParams("panel.positions.ExchangeTrading", 70, QuickTableColumnType.COL_DEFAULT, false, true),
        new ColumnParams("panel.positions.SLL", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, false, false, null, null, null, true),
        new ColumnParams("panel.positions.StartOfDayQty", 70, QuickTableColumnType.COL_SIMPLE_NUMERIC, false, true, false),
        new ColumnParams("panel.positions.AssetName", 70, QuickTableColumnType.COL_DEFAULT, false, true, true),
   /*50*/ new ColumnParams("panel.positions.Leverage", 70, QuickTableColumnType.COL_DEFAULT, false, false, true),
    ]

PositionItem.SL_COL_INDEX = 10;
PositionItem.TP_COL_INDEX = 11;
PositionItem.PRODUCTTYPE_COL_INDEX = 43;

PositionItem.GetItemId = function (pos)
{
    return pos.PositionId;
}

PositionItem.UseAccCurrency = function (pos)
{
    let useAccCurrency = true;

    if (!pos)
        return useAccCurrency

    let asset = pos.DataCache.userBaseCurrency;
    let assetAcc = pos.Account.BaseCurrency;

    if (asset !== assetAcc)
        useAccCurrency = false

    return useAccCurrency;
}

PositionItem.GetPositionAmountText = function (FPosition, sessionSettings, displayQtyAbsolute)
{
    if (!FPosition)
        return 0

    let sign = 1

    if (FPosition.BuySell !== OperationType.Buy && displayQtyAbsolute)
        sign = -1

    if (FPosition.Amount < 0 && sign < 0)
        sign = 1

    // TODO. fix rounding errors.
    if (GeneralSettings.View.DisplayQuantityInLots)
        return FPosition.Amount * sign
    else
    {
        let lotSize = FPosition.Instrument.getLotSize(FPosition.isOption(), FPosition.PutCall)
        return FPosition.Amount * lotSize * sign
    }
}

PositionItem.GetPositionSign = function (Position, displayQtyAbsolute = true)
{
    let sign = 1

    if (Position.BuySell !== OperationType.Buy && displayQtyAbsolute)
        sign = -1

    if (Position.Amount < 0 && sign < 0)
        sign = 1

    return sign;
}

PositionItem.GetPositionAmount = function (Position)
{
    if (Position != null)
        return PositionItem.GetAmount(Position.Amount, Position.Instrument);
    else
        return 0;
}

PositionItem.GetPositionStartOfDayAmount = function (Position)
{
    if (Position != null && Position.StartOfDayAmount !== null)
        return PositionItem.GetAmount(Position.StartOfDayAmount, Position.Instrument);
    else
        return 0;
}

PositionItem.GetAmount = function (amount, instrument)
{
    if (!GeneralSettings.View.DisplayQuantityInLots)
        return amount * instrument.getLotSize();
    else
        return amount;
}

PositionItem.prototype.NeedSortMenu = function ()
{
    return true;
}

PositionItem.prototype.GetMenuIndex = function (headerKey)
{
    let idx = PositionItem.ListMenuIndex.indexOf(headerKey)
    return idx === -1 ? 1000 : idx
}

PositionItem.ListMenuIndex =
    [
        'panel.positions.Account',
        'panel.positions.UserGroup',
        'panel.positions.Login',
        'panel.positions.route',
        'panel.positions.Symbol',
        'panel.positions.SymbolDescription',
        'panel.positions.type',
        'panel.positions.AssetName',
        'panel.positions.pos_number',
        'panel.positions.GroupId',
        'panel.positions.Operation',
        'panel.positions.Basis',
        'panel.positions.cur_price',
        'panel.positions.Qunatity',
        'panel.positions.StartOfDayQty',
        'panel.positions.date_time',
        'panel.positions.profit_usd',
        'panel.positions.profit_usd.InstrumentCurrency',
        'panel.positions.PL_ticks',
        'panel.positions.Net_PL',
        'panel.positions.Net_PL.InstrumentCurrency',
        'panel.positions.exposition_usd',
        'panel.positions.PositionValue',
        'panel.positions.commissions',
        'panel.positions.commissions.InstrumentCurrency',
        'panel.positions.swaps',
        'panel.positions.ExpDate',
        'panel.positions.strike',
        'panel.positions.SL',
        'panel.positions.TP',
        'panel.positions.SL_Value',
        'panel.positions.SLL',
        //'panel.positions.UsedMargin',     
        //'panel.positions.InitReq',
        //'panel.positions.MaintReq',
        //'panel.positions.MarginSuplus',
        'panel.positions.ExchangeTrading',
        'panel.positions.Leverage',
        'panel.positions.Close'
    ]